import React, { useState } from "react";
import { Box, Icon, Text, Tooltip, IconName } from "@telia/teddy";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PAGES from "../../../../../../constants/pages";
import { RootState } from "../../../../../../models";
import {
    isCustomerStepValidated,
    isEquipmentStepValidated,
    isSubscriptionStepValidated,
} from "../../../state/selectors/validationSelectors";
import { hasSubmittedOrder } from "../../../../app/state/selectors";
import { getIsD2DOrTMDealer, getSalesAgent } from "../../../state/selectors/selectors";
import DekningskartModal from "./DekningskartModal";
import SendFeedbackPanel from "../SendFeedbackPanel/SendFeedbackPanel";
import "./Sidebar.less";
import { SideMenu, SideMenuItem, SideMenuTop } from "../../../../components/components/molecules/SideMenu";

interface NavigationItem {
    icon?: IconName;
    label: string;
    path: string;
}

const navigationItems: NavigationItem[] = [
    {
        label: "Kunde",
        path: PAGES.RETAILER.ORDER.CUSTOMER.URL,
        icon: "end-user",
    },
    {
        label: "Abonnement",
        path: PAGES.RETAILER.ORDER.SUBSCRIPTION.URL,
        icon: "simcard",
    },
    {
        label: "Utstyr",
        path: PAGES.RETAILER.ORDER.EQUIPMENT.URL,
        icon: "devices",
    },
    {
        label: "Tilleggstjenester",
        path: PAGES.RETAILER.ORDER.ADDITIONAL_SERVICES.URL,
        icon: "services",
    },
    {
        label: "Oppsummering",
        path: PAGES.RETAILER.ORDER.SUMMARY.URL,
        icon: "summary",
    },
];

const navigationItemsBottom: NavigationItem[] = [
    {
        label: "Ny ordre",
        path: PAGES.RETAILER.NEWORDER.URL,
        icon: "plus",
    },
    {
        label: "Mine utkast",
        path: PAGES.RETAILER.ORDERS_OVERVIEW.URL,
        icon: "document-edit",
    },
];

const getStatuClassName = (status: string) => {
    switch (status) {
        case "error":
            return "validation-icon--alert";
        case "warning":
            return "validation-icon--warning";
        case "info":
            return "validation-icon--info";
        case "success":
            return "validation-icon--success";
        default:
            return "";
    }
};

const validationLabelMap: Record<string, string> = {
    Kunde: "customer",
    Abonnement: "subscription",
    Utstyr: "equipment",
};

const Sidebar: React.FC = () => {
    const location = useLocation();
    const [isReportErrorOpen, setIsReportErrorOpen] = useState(false);
    const [isDekningskartOpen, setIsDekningskartOpen] = useState(false);
    const isOrderSubmitted = useSelector((state: RootState) => hasSubmittedOrder(state));
    const customerStepValidation = useSelector((state: RootState) => isCustomerStepValidated(state));
    const subscriptionStepValidation = useSelector((state: RootState) => isSubscriptionStepValidated(state));
    const equipmentStepValidation = useSelector((state: RootState) => isEquipmentStepValidated(state));
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const isD2DOrTM = useSelector((state: RootState) => getIsD2DOrTMDealer(state));
    const validationByStep: Record<string, ReturnType<typeof isCustomerStepValidated>> = {
        customer: customerStepValidation,
        subscription: subscriptionStepValidation,
        equipment: equipmentStepValidation,
    };
    // D2D/TM dealers don't sell hardware, so the "Utstyr" step is not relevant for them.
    const visibleNavigationItems = isD2DOrTM
        ? navigationItems.filter((item) => item.label !== "Utstyr")
        : navigationItems;

    return (
        <div className="sidebar">
            <SideMenu className="sidebar__menu">
                <SideMenuTop>
                    {visibleNavigationItems.map((item) => {
                        const isDisabled =
                            ["Abonnement", "Tilleggstjenester", "Utstyr", "Oppsummering"].includes(item.label) &&
                            customerStepValidation?.status === "error";
                        const isActive =
                            location.pathname === item.path ||
                            (item.path === PAGES.RETAILER.ORDER.URL && location.pathname === PAGES.RETAILER.ORDER.URL);
                        return (
                            <SideMenuItem
                                label={item.label}
                                color="grey"
                                key={item.path}
                                icon={item.icon}
                                onClick={() => window.location.assign(item.path)}
                                active={isActive}
                                disabled={isDisabled || isOrderSubmitted}
                            >
                                <div className="validation-icon-wrapper">
                                    {(() => {
                                        const stepKey = validationLabelMap[item.label];
                                        const validation = stepKey ? validationByStep[stepKey] : null;
                                        if (!validation || (isActive && validation.status !== "success")) return null;

                                        const icon = (
                                            <Icon
                                                className={`validation-icon ${getStatuClassName(validation.status)}`}
                                                name={validation.icon}
                                                size="md"
                                                aria-hidden="true"
                                                focusable="false"
                                            />
                                        );

                                        return (
                                            <Box p="25">
                                                {validation.message ? (
                                                    <Tooltip>
                                                        <Tooltip.Trigger asChild>
                                                            <button
                                                                type="button"
                                                                className="sidebar__tooltip-trigger"
                                                                tabIndex={1}
                                                            >
                                                                {icon}
                                                            </button>
                                                        </Tooltip.Trigger>
                                                        <Tooltip.Content className="sidebar__tooltip-content">
                                                            {validation.message}
                                                        </Tooltip.Content>
                                                    </Tooltip>
                                                ) : (
                                                    icon
                                                )}
                                            </Box>
                                        );
                                    })()}
                                </div>
                            </SideMenuItem>
                        );
                    })}

                    {navigationItemsBottom.map((item, index) => {
                        const isActive =
                            location.pathname === item.path ||
                            (item.path === PAGES.RETAILER.ORDER.URL && location.pathname === PAGES.RETAILER.ORDER.URL);
                        return (
                            <SideMenuItem
                                className={index === 0 ? "sidebar__menu-item--bottom" : ""}
                                label={item.label}
                                color="grey"
                                key={item.path}
                                icon={item.icon}
                                onClick={() => window.location.assign(item.path)}
                                active={isActive}
                            />
                        );
                    })}
                    <SideMenuItem
                        label="Gi tilbakemelding"
                        color="grey"
                        icon="send"
                        onClick={() => setIsReportErrorOpen(true)}
                    />
                    <SideMenuItem
                        label="Dekningskart"
                        color="grey"
                        icon="wireless"
                        onClick={() => setIsDekningskartOpen(true)}
                    />
                    <SideMenuItem
                        label="Priser"
                        color="grey"
                        icon="info"
                        onClick={() =>
                            window.open(
                                "/mobilabonnement/priser/#plus",
                                "priser",
                                "width=1200,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes",
                            )
                        }
                    />
                    {salesAgent?.dealerName && (
                        <div className="sidebar__dealer-name">
                            <Text variant="additional-100">
                                {salesAgent.dealerName}
                                {salesAgent.dealerCode ? ` (${salesAgent.dealerCode})` : ""}
                            </Text>
                        </div>
                    )}
                </SideMenuTop>
            </SideMenu>
            <DekningskartModal isOpen={isDekningskartOpen} onClose={() => setIsDekningskartOpen(false)} />
            <SendFeedbackPanel open={isReportErrorOpen} onOpenChange={setIsReportErrorOpen} />
        </div>
    );
};

export default Sidebar;
